import type { ReactElement, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { Flex, Radio, Spinner } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { ShippingMethodId } from '../../../../../bff-api/baskets/model/ShippingMethodId';
import { useBasket } from '../../../../../bff-api/baskets/useBasket';
import {
    getShippingAddressByMethodId,
    hasGiftRegistryProducts,
} from '../../../../../bff-api/utils/basket-utils';
import { useSDDAddress } from '../../../../../hooks/useSDDAddress';
import { useSameDayDelivery } from '../../../../../hooks/useSameDayDelivery';
import { Text } from '../../../../../ui-components';
import { isDropship } from '../../../../../utils/product-utils';
import type { ProductItem, ShippingOption } from '../../../../../bff-api/baskets/model';
import type { ProductInventory } from '../../../../../bff-api/baskets/model/BasketInventory';
import type { Store } from '../../../../../bff-api/stores/model';

interface Props {
    id: string;
    productItem: ProductItem;
    option?: ShippingOption;
    inventory?: ProductInventory;
    sddStore?: Store;
    onOpenLocationSearch: () => void;
    isLoading: boolean;
}

export const CartSDDOption = ({
    id,
    productItem,
    option,
    inventory,
    sddStore,
    onOpenLocationSearch,
    isLoading,
}: Props): ReactElement | null => {
    const { data: basket } = useBasket();
    const address = useSDDAddress().data;
    const sddAddress = getShippingAddressByMethodId(basket, ShippingMethodId.SAME_DAY_DELIVERY);
    const { description: sameDayDeliveryTitle, isNextDayDelivery } = useSameDayDelivery({
        preferenceForBasketValue: true,
    });

    const zipCode = (sddAddress || address)?.postalCode;

    const titleComponent = useMemo(
        () => (
            <Text id={`${id}-label`} style="bold">
                {sameDayDeliveryTitle}
            </Text>
        ),
        [id, sameDayDeliveryTitle],
    );

    const sddComponent = useCallback(
        (chunks: ReactNode[]) => (
            <Text
                id={`${id}-postalCode`}
                size="small"
                style="bold"
                type="link"
                onClick={onOpenLocationSearch}
            >
                {chunks}
            </Text>
        ),
        [id, onOpenLocationSearch],
    );

    if (!option || !inventory) {
        return null;
    }

    if (
        !option.enabled ||
        !productItem.c_online ||
        isDropship(productItem) ||
        hasGiftRegistryProducts(basket)
    ) {
        return (
            <Flex data-testid={`${id}-not-available`} gap={2} alignItems="start" opacity={0.5}>
                <Radio data-testid={`${id}-radio`} value={option.type} width={4} isDisabled />

                <Flex direction="column">
                    {titleComponent}

                    <Text id={`${id}-not-available-label`} size="small">
                        <FormattedMessage id="not.available" defaultMessage="Not Available" />
                    </Text>
                </Flex>
            </Flex>
        );
    }

    if (!sddStore || !zipCode) {
        return (
            <Flex data-testid={`${id}-store-not-available`} gap={2} alignItems="start">
                <Radio data-testid={`${id}-radio`} value={option.type} width={4} isDisabled />

                <Flex direction="column">
                    {titleComponent}

                    <Text
                        id={`${id}-enter-zip-code`}
                        size="small"
                        style="bold"
                        type="link"
                        onClick={onOpenLocationSearch}
                    >
                        <FormattedMessage
                            id="edd.enter.zip.code"
                            defaultMessage="Enter your Zip Code"
                        />
                    </Text>
                </Flex>
            </Flex>
        );
    }

    const hasEnoughQuantity = inventory?.sddInventory?.availableToSell >= productItem.quantity;

    return isLoading || (sddStore.c_isSameDayDeliveryEligible && hasEnoughQuantity) ? (
        <Flex data-testid={`${id}-available`} gap={2} alignItems="start">
            {isLoading ? (
                <Spinner data-testid={`${id}-spinner`} size="sm" marginTop={1} />
            ) : (
                <Radio data-testid={`${id}-radio`} value={option.type} width={4} />
            )}

            <Flex direction="column">
                {titleComponent}

                <Text id={`${id}-deliver-to-zip-code`}>
                    <FormattedMessage
                        id="sdd.deliver.to.zipCode"
                        defaultMessage="Deliver to <sddComponent>{zipCode}</sddComponent>"
                        values={{ sddComponent, zipCode }}
                    />
                </Text>

                <Text id={`${id}-order-now-get-today`} size="small" color="slt-success-green.1000">
                    {isNextDayDelivery ? (
                        <FormattedMessage
                            id="sdd.order.now.get.tomorrow"
                            defaultMessage="Order now, get it tomorrow"
                        />
                    ) : (
                        <FormattedMessage
                            id="sdd.order.now.get.today"
                            defaultMessage="Order now, get it today"
                        />
                    )}
                </Text>
            </Flex>
        </Flex>
    ) : (
        <Flex data-testid={`${id}-not-available-for-zip-code`} gap={2} alignItems="start">
            <Radio data-testid={`${id}-radio`} value={option.type} width={4} isDisabled />

            <Flex direction="column">
                {titleComponent}

                <Flex gap={1} alignItems="end" wrap="wrap">
                    <Text id={`${id}-not-available.for`}>
                        <FormattedMessage
                            id="not.available.for"
                            defaultMessage="Not available for"
                        />
                    </Text>

                    <Text
                        id={`${id}-enter-zip-code`}
                        size="small"
                        style="bold"
                        type="link"
                        onClick={onOpenLocationSearch}
                    >
                        {zipCode}
                    </Text>
                </Flex>
            </Flex>
        </Flex>
    );
};

CartSDDOption.defaultProps = {
    option: undefined,
    inventory: undefined,
    sddStore: undefined,
};
